// web/src/screens/final.ts — UI-06 final/finish screen (plan 04-10).
//
// Responsibilities:
//   1. POST /api/sessions/:id/finish to close the session and retrieve stats.
//   2. Render the celebration UI (UI-SPEC §"Final screen").
//   3. Call releaseMic() — UI-07 states the mic stream is released here
//      (and ONLY here), not on card screen unmount.
//
// The CTA layout is configurable via localStorage `finalScreenMode` (set
// from /config). Two demo-tunable modes:
//   - "next" (default): "Spēlēt vēlreiz" + "Nākamā spēle" buttons with a
//     "← Atpakaļ uz spēļu sarakstu" link below. On the last game in the
//     picker order, "Nākamā spēle" is replaced by an "Atpakaļ uz spēļu
//     sarakstu" button (and the link is dropped — the button covers it).
//   - "other": legacy "Spēlēt vēlreiz" + "Cita spēle" pair (both navigate
//     to /picker).
//
// No confetti in Phase 4 (post-MVP per CONTEXT.md); Motion scale reveal
// on "Lieliski!" is the celebration.

import { animate, type DOMKeyframesDefinition } from "motion";
import { releaseMic } from "../components/mic.js";
import { finishSession, startSession } from "../api/sessions.js";
import { navigate } from "../main.js";
import { TILES, gameIdFor } from "./picker.js";
import type { FinishResponse } from "../types/api.js";

export type FinalScreenMode = "next" | "other";
const MODE_STORAGE_KEY = "finalScreenMode";

export function readFinalScreenMode(): FinalScreenMode {
  try {
    const v = localStorage.getItem(MODE_STORAGE_KEY);
    if (v === "other") return "other";
  } catch { /* ignore — private mode etc. */ }
  return "next";
}

export function writeFinalScreenMode(mode: FinalScreenMode): void {
  try {
    localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch { /* ignore */ }
}

const PRIMARY_BTN_STYLE = `
  min-height: 72px; min-width: 200px;
  padding: 16px 24px;
  font-size: clamp(22px, 5.5vw, 32px); font-weight: 700;
  color: var(--text-primary); background: var(--surface);
  border: 3px solid var(--theme-color);
  border-radius: 24px; cursor: pointer;
  font-family: var(--font-fancy);
`;

export function mount(root: HTMLElement, props?: unknown): () => void {
  const p = (props ?? {}) as { sessionId?: string };
  const sessionId = p.sessionId ?? "";

  // UI-07: release mic stream here — this is the ONLY place releaseMic is called
  // across the entire app. Releasing on card-screen unmount would revoke the iOS
  // implicit grant and force the OS sheet on the next session start.
  releaseMic().catch(() => { /* non-fatal — stream may already be gone */ });

  const sess = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("currentSession") ?? "{}") as {
        gameShortCode?: string;
        gameId?: string;
      };
    } catch { return {}; }
  })();

  const mode = readFinalScreenMode();
  const currentIdx = TILES.findIndex(t => t.shortCode === sess.gameShortCode);
  const isLast = currentIdx >= 0 && currentIdx === TILES.length - 1;
  const nextTile = currentIdx >= 0 && !isLast ? TILES[currentIdx + 1] : null;

  // Build the CTA row + optional back-link based on mode and position.
  let ctasHtml = `<button class="btn-replay" style="${PRIMARY_BTN_STYLE}">Spēlēt vēlreiz</button>`;
  let backLinkHtml = "";

  if (mode === "other") {
    ctasHtml += `<button class="btn-other" style="${PRIMARY_BTN_STYLE}">Cita spēle</button>`;
  } else if (isLast || !nextTile) {
    // "next" mode on the last game (or unknown current game) — second
    // button takes the role of the back-link.
    ctasHtml += `<button class="btn-back-list" style="${PRIMARY_BTN_STYLE}">Atpakaļ uz spēļu sarakstu</button>`;
  } else {
    ctasHtml += `<button class="btn-next" style="${PRIMARY_BTN_STYLE}">Nākamā spēle</button>`;
    backLinkHtml = `
      <button class="btn-back-link" style="
        background: none; border: 0;
        font-size: 22px; color: var(--text-muted);
        padding: var(--space-sm) var(--space-md);
        min-height: 44px; cursor: pointer;
        font-family: var(--font-fancy);
        margin-top: var(--space-md);
      ">← Atpakaļ uz spēļu sarakstu</button>
    `;
  }

  root.innerHTML = `
    <section class="screen-final" style="
      min-height: 100dvh;
      background: var(--bg);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: var(--space-lg);
      padding: var(--space-2xl) var(--space-lg);
      box-sizing: border-box;
      text-align: center;
    ">
      <div class="trophy" style="font-size: 96px; line-height: 1;" aria-hidden="true">🏆</div>

      <p class="stats-line" style="
        font-size: 32px; font-weight: 600; line-height: 1.2;
        color: var(--text-primary); letter-spacing: 0.02em;
        font-family: var(--font-fancy);
      ">Ielādē...</p>

      <p class="celebration" style="
        font-size: 96px; font-weight: 700; line-height: 1.1;
        color: var(--theme-color); letter-spacing: 0.02em;
        font-family: var(--font-fancy);
        transform: scale(0.8); opacity: 0;
      ">Lieliski!</p>

      <div class="cta-row" style="
        display: flex; gap: var(--space-md);
        flex-wrap: wrap; justify-content: center;
        margin-top: var(--space-2xl);
      ">${ctasHtml}</div>

      ${backLinkHtml}
    </section>
  `;

  const statsLine   = root.querySelector(".stats-line")!    as HTMLParagraphElement;
  const celebration = root.querySelector(".celebration")!   as HTMLParagraphElement;
  const btnReplay   = root.querySelector(".btn-replay")!    as HTMLButtonElement;
  const btnOther    = root.querySelector(".btn-other")      as HTMLButtonElement | null;
  const btnNext     = root.querySelector(".btn-next")       as HTMLButtonElement | null;
  const btnBackList = root.querySelector(".btn-back-list")  as HTMLButtonElement | null;
  const btnBackLink = root.querySelector(".btn-back-link")  as HTMLButtonElement | null;

  // Animate "Lieliski!" reveal (Animation Contract: scale 0.8→1.0 + opacity 0→1, 200 ms ease-out)
  // Explicit DOMKeyframesDefinition forces the DOM-element overload
  const revealKf: DOMKeyframesDefinition = { scale: [0.8, 1.0], opacity: [0, 1] };
  animate(celebration as Element, revealKf, { duration: 0.2, ease: "easeOut" });

  // UI-06 confetti: 14 particles spawned at trophy center.
  // W4: track particles in a closure-scoped array so the unmount fn can
  // sweep any in-flight particles (the .then(remove) handles normal
  // completion; sweep handles mid-animation unmount).
  const confettiParticles: HTMLDivElement[] = [];
  function spawnConfetti(): void {
    const trophy = root.querySelector(".trophy") as HTMLElement | null;
    if (!trophy) return;
    const r = trophy.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const themeColors = ["#5E3FBE", "#B43FA8", "#B43FA8", "#3F8E96", "#0F5C7A", "#D72E2E"];
    const N = 14;
    for (let i = 0; i < N; i++) {
      const dot = document.createElement("div");
      dot.style.cssText = `
        position: fixed;
        left: ${cx}px; top: ${cy}px;
        width: 10px; height: 10px;
        border-radius: 50%;
        background: ${themeColors[i % themeColors.length]};
        pointer-events: none;
        z-index: 100;
        transform: translate(-50%, -50%);
      `;
      document.body.appendChild(dot);
      confettiParticles.push(dot);
      const angle = (Math.PI * 2 * i) / N + (Math.random() - 0.5) * 0.6;
      const dist = 80 + Math.random() * 60;
      const dx = Math.cos(angle) * dist;
      const dyMid = Math.sin(angle) * dist - 40;
      const dyEnd = dyMid + 80;
      const kf: DOMKeyframesDefinition = {
        x: [0, dx, dx],
        y: [0, dyMid, dyEnd],
        opacity: [1, 1, 0],
        scale: [1, 1, 0.3],
      };
      animate(dot as Element, kf, {
        duration: 0.6,
        delay: i * 0.03,
        ease: ["easeOut", "easeIn"],
      }).then(() => {
        dot.remove();
        const idx = confettiParticles.indexOf(dot);
        if (idx >= 0) confettiParticles.splice(idx, 1);
      });
    }
  }
  spawnConfetti();

  // POST /finish to close session + retrieve stats
  if (sessionId) {
    finishSession(sessionId)
      .then((resp: FinishResponse) => {
        // UI-07 lock: "X = correct + partial"; the third bucket exists on
        // the wire but is never named to the child. Phase 9 v2 nests stats
        // under resp.stats — see internal/turn/handler.go FinishSessionHandler.
        const { correct, partial, total } = resp.stats;
        const celebratory = correct + partial;
        statsLine.textContent = `Tu uzminēji ${celebratory} no ${total} vārdiem!`;
      })
      .catch(() => {
        // Non-fatal — kid still sees the celebration; stats just stay as "..."
        statsLine.textContent = "Tu spēlēji lieliski!";
      });
  } else {
    statsLine.textContent = "Tu spēlēji lieliski!";
  }

  function goToPicker(): void {
    sessionStorage.removeItem("currentSession");
    navigate("/picker");
  }

  async function startGameByShortCode(
    shortCode: string,
    themeClass: string | null,
    btn: HTMLButtonElement | null,
  ): Promise<void> {
    const gameId = gameIdFor(shortCode);
    if (!gameId) {
      // Same fallback as picker when __GAMES__ is missing the entry.
      console.warn("final: no game UUID for short_code", shortCode);
      goToPicker();
      return;
    }
    if (btn) btn.disabled = true;
    if (themeClass) document.body.className = themeClass;
    try {
      const resp = await startSession({ game_id: gameId });
      // v2 currentSession shape (10-09): {sessionId, gameShortCode, themeColor}.
      // The themeColor is sourced from picker.TILES so card.ts can read it
      // without recomputing from the body class.
      const tile = TILES.find((t) => t.shortCode === shortCode);
      sessionStorage.setItem("currentSession", JSON.stringify({
        sessionId: resp.session_id,
        gameShortCode: shortCode,
        themeColor: tile?.themeColor ?? "",
        totalCards: resp.total_cards,
      }));
      navigate("/preflight");
    } catch (err) {
      console.error("final: startSession failed", err);
      if (btn) btn.disabled = false;
      // Soft-fall back to the picker so the user is never stuck on the
      // final screen if a game start hits a transient error.
      goToPicker();
    }
  }

  async function replayGame(): Promise<void> {
    const currentTile = currentIdx >= 0 ? TILES[currentIdx] : null;
    if (!sess.gameShortCode) { goToPicker(); return; }
    await startGameByShortCode(
      sess.gameShortCode,
      currentTile?.themeClass ?? null,
      btnReplay,
    );
  }

  async function startNextGame(): Promise<void> {
    if (!nextTile) { goToPicker(); return; }
    await startGameByShortCode(nextTile.shortCode, nextTile.themeClass, btnNext);
  }

  const onReplay = (): void => { void replayGame(); };
  const onNext   = (): void => { void startNextGame(); };

  btnReplay.addEventListener("click", onReplay);
  btnOther?.addEventListener("click", goToPicker);
  btnBackList?.addEventListener("click", goToPicker);
  btnBackLink?.addEventListener("click", goToPicker);
  btnNext?.addEventListener("click", onNext);

  return (): void => {
    // W4: sweep any in-flight confetti particles if final unmounts mid-animation.
    for (const dot of confettiParticles) dot.remove();
    confettiParticles.length = 0;
    btnReplay.removeEventListener("click", onReplay);
    btnOther?.removeEventListener("click", goToPicker);
    btnBackList?.removeEventListener("click", goToPicker);
    btnBackLink?.removeEventListener("click", goToPicker);
    btnNext?.removeEventListener("click", onNext);
    // mic already released at mount
  };
}
